import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import { printDispatchDocument } from './printDispatchDocuments';

const SOURCE_MANUAL = 1;
const SOURCE_FROM_ORDER = 2;
const SOURCE_DIRECT = 3;
const STATUS_DRAFT = 1;

const SOURCE_OPTIONS = [
    { value: SOURCE_MANUAL, label: 'يدوي' },
    { value: SOURCE_FROM_ORDER, label: 'من طلب معتمد' },
    { value: SOURCE_DIRECT, label: 'مباشر (طوارئ)' },
];

export default {
    name: 'DispatchFormV2',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.canManage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية إدارة الصرف');
            this.$router.push('/DispatchV2');
            return;
        }
        await Promise.all([this.loadBranches(), this.loadStockings()]);
        const id = this.$route.params.id;
        if (id) {
            this.isEditMode = true;
            await this.loadDispatch(Number(id));
        } else {
            this.isEditMode = false;
            const q = this.$route.query || {};
            if (q.sourceType) this.form.sourceType = Number(q.sourceType);
            if (q.orderId) {
                this.form.sourceType = SOURCE_FROM_ORDER;
                this.form.sourceMedicineOrderId = Number(q.orderId);
            }
            if (this.form.sourceType !== SOURCE_FROM_ORDER) {
                this.addEmptyLine();
            } else if (this.form.sourceMedicineOrderId && this.form.sourceStockingId) {
                await this.loadApprovedOrdersList();
                await this.loadLinesFromOrder();
            }
        }
    },

    watch: {
        'form.sourceStockingId'(val) {
            if (val) {
                this.refreshProductCatalog();
                if (this.form.sourceType === SOURCE_FROM_ORDER && this.form.sourceMedicineOrderId) {
                    this.loadLinesFromOrder();
                }
            }
        },
        'form.sourceType'(val) {
            if (val === SOURCE_FROM_ORDER) {
                this.lines = [];
            } else if (this.lines.length === 0) {
                this.addEmptyLine();
            }
            if (val !== SOURCE_FROM_ORDER) {
                this.form.sourceMedicineOrderId = '';
            }
        },
        'form.sourceMedicineOrderId'(val) {
            if (this.form.sourceType === SOURCE_FROM_ORDER && val && this.form.sourceStockingId) {
                this.loadLinesFromOrder();
            }
        },
    },

    computed: {
        canManage() {
            return this.hasInventoryAndStockAccessUi() || this.canDispenseFromMinistryUi();
        },
        isReadOnly() {
            return this.isEditMode && this.detail && Number(this.detail.status) !== STATUS_DRAFT;
        },
        pageTitle() {
            if (this.isEditMode && this.detail && this.detail.code) return `صرف V2 — ${this.detail.code}`;
            return 'إنشاء صرف جديد (V2)';
        },
        sourceOptions() { return SOURCE_OPTIONS; },
        ministryStockings() {
            return (this.stockingsList || []).filter(s => !s.branchesId && s.status === 1);
        },
        facilityBranches() {
            return (this.branchesList || []).filter(b => b.status !== 9);
        },
        isOrderMode() {
            return this.form.sourceType === SOURCE_FROM_ORDER;
        },
        canPrintIssue() {
            return this.detail && this.detail.capabilities && this.detail.capabilities.canPrintIssueVoucher;
        },
        canPrintFinal() {
            return this.detail && this.detail.capabilities && this.detail.capabilities.canPrintFinalInvoice;
        },
        linesGrandTotal() {
            return this.lines.reduce((s, ln) => s + this.lineTotal(ln), 0);
        },
    },

    data() {
        return {
            isEditMode: false,
            loading: false,
            saving: false,
            detail: null,
            branchesList: [],
            stockingsList: [],
            productCatalog: [],
            approvedOrders: [],
            modalAttachments: [],
            form: {
                facilityBranchesId: '',
                sourceStockingId: '',
                sourceType: SOURCE_MANUAL,
                sourceMedicineOrderId: '',
                note: '',
            },
            lines: [],
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        numberFormat(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n.toLocaleString('en-US') : '—';
        },
    },

    methods: {
        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        lineTotal(line) {
            const q = Number(line.quantity) || 0;
            const p = Number(line.unitPrice);
            if (!q || !Number.isFinite(p)) return 0;
            return Math.round(q * p * 1000) / 1000;
        },

        formatLineTotal(line) {
            const t = this.lineTotal(line);
            return t ? t.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 }) : '—';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch { this.branchesList = []; }
        },

        async loadStockings() {
            try {
                const res = await this.$http.GetAllStocking();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.stockingsList = Array.isArray(arr) ? arr : [];
            } catch { this.stockingsList = []; }
        },

        async refreshProductCatalog() {
            if (!this.form.sourceStockingId) {
                this.productCatalog = [];
                return;
            }
            try {
                const res = await this.$http.GetDispatchAvailableProducts(this.form.sourceStockingId, '');
                this.productCatalog = (res && res.data) || [];
            } catch { this.productCatalog = []; }
        },

        buildEmptyLine() {
            return {
                key: Date.now() + Math.random(),
                lineId: null,
                nationalFormularyItemId: null,
                nationalScientificName: '',
                nationalCode: '',
                quantityApproved: null,
                lockedNational: false,
                productsId: '',
                commercialOptions: [],
                tradeName: '',
                manufacturerName: '',
                countryName: '',
                batches: [],
                serialNumber: '',
                productsCountId: null,
                batchReadOnly: false,
                expiryDate: '',
                warehouseQty: 0,
                quantity: 0,
                unitPrice: '',
                sourceMedicineOrderLineId: null,
            };
        },

        addEmptyLine() {
            const ln = this.buildEmptyLine();
            if (!this.isOrderMode) {
                ln.commercialOptions = this.productCatalog.map(p => ({
                    id: p.id,
                    tradeName: p.tradeName,
                    manufacturerName: p.manufacturerName,
                    countryName: p.countryName || '',
                    unitPrice: p.unitPrice,
                    scientificName: p.scientificName,
                }));
            }
            this.lines.push(ln);
        },

        removeLine(idx) {
            if (this.isOrderMode && this.lines.length <= 1) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يجب الإبقاء على صف واحد على الأقل من أصناف الطلب');
                return;
            }
            this.lines.splice(idx, 1);
            if (this.lines.length === 0 && !this.isOrderMode) this.addEmptyLine();
        },

        async loadApprovedOrdersList() {
            if (!this.form.facilityBranchesId) return;
            try {
                const res = await this.$http.GetDispatchApprovedOrders(this.form.facilityBranchesId);
                this.approvedOrders = (res && res.data) || [];
            } catch { this.approvedOrders = []; }
        },

        async loadLinesFromOrder() {
            if (!this.form.sourceMedicineOrderId || !this.form.sourceStockingId) return;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetDispatchV2ApprovedOrderItems(this.form.sourceMedicineOrderId);
                const data = (res && res.data) || {};
                const items = data.items || data.Items || [];
                this.lines = [];
                for (const it of items) {
                    const ln = this.buildEmptyLine();
                    ln.nationalFormularyItemId = it.nationalFormularyItemId;
                    ln.nationalScientificName = it.scientificName;
                    ln.nationalCode = it.nationalCode;
                    ln.quantityApproved = it.quantityApproved;
                    ln.lockedNational = true;
                    ln.sourceMedicineOrderLineId = it.primaryOrderLineId;
                    await this.loadCommercialOptionsForLine(ln);
                    this.lines.push(ln);
                }
                if (this.lines.length === 0) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد أصناف معتمدة في هذا الطلب');
                }
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        async loadCommercialOptionsForLine(line) {
            if (!line.nationalFormularyItemId || !this.form.sourceStockingId) return;
            try {
                const res = await this.$http.GetProductsByNationalFormularyItem(
                    line.nationalFormularyItemId,
                    this.form.sourceStockingId,
                );
                line.commercialOptions = (res && res.data) || [];
            } catch { line.commercialOptions = []; }
        },

        async onCommercialSelected(line) {
            const prod = (line.commercialOptions || []).find(p => p.id === Number(line.productsId))
                || this.productCatalog.find(p => p.id === Number(line.productsId));
            if (!prod) return;
            line.tradeName = prod.tradeName || prod.name;
            line.manufacturerName = prod.manufacturerName || '';
            line.countryName = prod.countryName || prod.countryRegionName || '';
            if (!line.lockedNational && prod.scientificName) {
                line.nationalScientificName = prod.scientificName;
            }
            line.unitPrice = prod.unitPrice != null ? prod.unitPrice : '';
            line.serialNumber = '';
            line.productsCountId = null;
            line.batchReadOnly = false;
            line.batches = [];
            line.warehouseQty = 0;
            line.expiryDate = '';
            if (!line.countryName && line.productsId) {
                try {
                    const pkgRes = await this.$http.GetProductWithPackagings(line.productsId);
                    const info = (pkgRes && pkgRes.data && pkgRes.data.info) || {};
                    line.countryName = info.countryRegionName || info.countryName || '';
                } catch { /* ignore */ }
            }
            await this.loadBatchesForLine(line);
        },

        async loadBatchesForLine(line) {
            if (!line.productsId || !this.form.sourceStockingId) return;
            try {
                const res = await this.$http.GetDispatchV2ProductBatches(line.productsId, this.form.sourceStockingId);
                line.batches = (res && res.data) || [];
                if (line.batches.length === 1) {
                    this.applyBatchToLine(line, line.batches[0]);
                    line.batchReadOnly = true;
                } else {
                    line.batchReadOnly = false;
                }
            } catch {
                line.batches = [];
                line.batchReadOnly = false;
            }
        },

        applyBatchToLine(line, batch) {
            line.serialNumber = batch.serialNumber || batch.batchNumber || '';
            line.productsCountId = batch.productsCountId || batch.id;
            line.expiryDate = batch.expiryDate ? moment(batch.expiryDate).format('YYYY-MM-DD') : '';
            line.warehouseQty = batch.availableQuantity || 0;
            if (batch.unitPrice != null || batch.price != null) {
                line.unitPrice = batch.unitPrice != null ? batch.unitPrice : batch.price;
            }
        },

        onBatchSelected(line) {
            const batch = (line.batches || []).find(b =>
                (b.serialNumber || b.batchNumber) === line.serialNumber
                || String(b.id) === String(line.productsCountId));
            if (batch) this.applyBatchToLine(line, batch);
        },

        onQuantityInput(line) {
            const qty = Number(line.quantity) || 0;
            const max = Number(line.warehouseQty) || 0;
            if (qty > max && max > 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', `الكمية تتجاوز المتوفر في المخزن (${max})`);
                line.quantity = max;
            }
        },

        async loadDispatch(id) {
            this.loading = true;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetDispatchOne(id);
                this.detail = (res && res.data) || null;
                if (!this.detail) {
                    this.$helper.ShowMessage('error', 'خطأ', 'السند غير موجود');
                    this.$router.push('/DispatchV2');
                    return;
                }
                this.form.facilityBranchesId = this.detail.facilityBranchesId;
                this.form.sourceStockingId = this.detail.sourceStockingId;
                this.form.sourceType = this.detail.dispatchSourceType;
                this.form.sourceMedicineOrderId = this.detail.sourceMedicineOrderId || '';
                this.form.note = this.detail.note || '';
                await this.refreshProductCatalog();
                if (this.form.sourceType === SOURCE_FROM_ORDER) {
                    await this.loadApprovedOrdersList();
                }
                this.lines = (this.detail.lines || []).map(ln => ({
                    key: ln.id,
                    lineId: ln.id,
                    nationalFormularyItemId: null,
                    nationalScientificName: '',
                    nationalCode: '',
                    quantityApproved: null,
                    lockedNational: false,
                    productsId: ln.productsId,
                    commercialOptions: [],
                    tradeName: ln.productName || '',
                    manufacturerName: '',
                    countryName: '',
                    batches: [],
                    serialNumber: ln.serialNumber || '',
                    productsCountId: null,
                    batchReadOnly: !!ln.serialNumber,
                    expiryDate: ln.expiryDate ? moment(ln.expiryDate).format('YYYY-MM-DD') : '',
                    warehouseQty: 0,
                    quantity: ln.quantity,
                    unitPrice: ln.unitPrice,
                    sourceMedicineOrderLineId: ln.sourceMedicineOrderLineId,
                }));
                for (const ln of this.lines) {
                    if (ln.productsId) await this.loadBatchesForLine(ln);
                }
                if (this.lines.length === 0) this.addEmptyLine();
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                this.$router.push('/DispatchV2');
            } finally {
                this.loading = false;
                this.$blockUI.Stop();
            }
        },

        OnAttachmentChange(event) {
            const files = (event && event.target && event.target.files) || [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                const r = new FileReader();
                r.onload = () => {
                    this.modalAttachments.push({ FileName: f.name, ContentType: f.type, Base64Content: r.result });
                };
                r.readAsDataURL(f);
            }
        },

        validateForm() {
            if (!this.form.facilityBranchesId || !this.form.sourceStockingId) {
                return 'الرجاء اختيار الجهة المستفيدة والمخزن المصدر';
            }
            if (this.form.sourceType === SOURCE_FROM_ORDER && !this.form.sourceMedicineOrderId) {
                return 'الرجاء اختيار طلب معتمد';
            }
            if (this.form.sourceType === SOURCE_DIRECT && !this.modalAttachments.length && !this.isEditMode) {
                return 'الصرف المباشر يتطلب رفع المستندات التعزيزية';
            }
            const activeLines = this.lines.filter(l => l.productsId && Number(l.quantity) > 0);
            if (activeLines.length === 0) return 'الرجاء إضافة صنف واحد على الأقل بكمية صحيحة';
            for (const ln of activeLines) {
                if (!ln.serialNumber && ln.batches.length > 1) {
                    return `الرجاء اختيار تشغيلة للصنف "${ln.tradeName}"`;
                }
                if (Number(ln.quantity) > Number(ln.warehouseQty || 0)) {
                    return `الكمية تتجاوز المتوفر للصنف "${ln.tradeName}"`;
                }
            }
            return null;
        },

        buildLinesPayload() {
            return this.lines
                .filter(l => l.productsId && Number(l.quantity) > 0)
                .map(ln => ({
                    ProductsId: Number(ln.productsId),
                    Quantity: Number(ln.quantity),
                    UnitPrice: ln.unitPrice === '' || ln.unitPrice == null ? null : Number(ln.unitPrice),
                    SerialNumber: ln.serialNumber || null,
                    ExpiryDate: ln.expiryDate || null,
                    SourceMedicineOrderLineId: ln.sourceMedicineOrderLineId || null,
                }));
        },

        async saveDispatch() {
            const err = this.validateForm();
            if (err) {
                this.$helper.ShowMessage('warning', 'تنبيه', err);
                return;
            }
            this.saving = true;
            this.$blockUI.Start();
            try {
                if (this.isEditMode && this.detail && Number(this.detail.status) === STATUS_DRAFT) {
                    await this.syncLinesOnEdit();
                    this.$helper.ShowMessage('success', 'نجاح', 'تم حفظ التعديلات');
                    await this.loadDispatch(this.detail.id);
                } else if (!this.isEditMode) {
                    const payload = {
                        SourceStockingId: Number(this.form.sourceStockingId),
                        FacilityBranchesId: Number(this.form.facilityBranchesId),
                        DispatchSourceType: Number(this.form.sourceType),
                        SourceMedicineOrderId: this.form.sourceMedicineOrderId ? Number(this.form.sourceMedicineOrderId) : null,
                        Note: this.form.note || '',
                        Lines: this.buildLinesPayload(),
                        Attachments: this.form.sourceType === SOURCE_DIRECT ? this.modalAttachments : null,
                    };
                    const res = await this.$http.DispatchCreate(payload);
                    const id = res && res.data && res.data.id;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف');
                    if (id) this.$router.replace({ name: 'DispatchV2Edit', params: { id } });
                    else this.$router.push('/DispatchV2');
                }
            } catch (e) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(e));
            } finally {
                this.saving = false;
                this.$blockUI.Stop();
            }
        },

        async syncLinesOnEdit() {
            const dispatchId = this.detail.id;
            const serverLines = (this.detail.lines || []).map(l => l.id);
            const localWithId = this.lines.filter(l => l.lineId && l.productsId && Number(l.quantity) > 0);
            const localIds = localWithId.map(l => l.lineId);
            for (const sid of serverLines) {
                if (!localIds.includes(sid)) {
                    await this.$http.DispatchRemoveLine({ DispatchId: dispatchId, LineId: sid });
                }
            }
            for (const ln of this.lines) {
                if (!ln.productsId || Number(ln.quantity) <= 0) continue;
                const body = {
                    DispatchId: dispatchId,
                    LineId: ln.lineId,
                    ProductsId: Number(ln.productsId),
                    Quantity: Number(ln.quantity),
                    UnitPrice: ln.unitPrice === '' || ln.unitPrice == null ? null : Number(ln.unitPrice),
                    SerialNumber: ln.serialNumber || null,
                    ExpiryDate: ln.expiryDate || null,
                    SourceMedicineOrderLineId: ln.sourceMedicineOrderLineId || null,
                };
                if (ln.lineId) await this.$http.DispatchUpdateLine(body);
                else await this.$http.DispatchAddLine(body);
            }
        },

        goBack() {
            this.$router.push('/DispatchV2');
        },

        printIssue() {
            if (!this.detail) return;
            Swal.fire({
                title: 'تأكيد طباعة سند الصرف',
                text: 'سيتم خصم الكميات من المخزن المصدر.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintIssueVoucher(this.detail.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم خصم الكميات');
                        await this.loadDispatch(this.detail.id);
                        printDispatchDocument(this.detail, 'issue', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        printFinal() {
            if (!this.detail) return;
            Swal.fire({
                title: 'تأكيد طباعة الفاتورة النهائية',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintFinalInvoice(this.detail.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم طباعة الفاتورة النهائية');
                        await this.loadDispatch(this.detail.id);
                        printDispatchDocument(this.detail, 'finalInvoice', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },
    },
};
